export type Stimmung = 'neutral' | 'staunt' | 'begeistert' | 'trost' | 'feiert'

/**
 * Pippa, der Begleitballon.
 *
 * Gezeichnet nach dem Kindchenschema: überproportional große Augen, weit
 * auseinander und tief im Gesicht, runde Formen, winziger Mund. Dasselbe
 * Rezept steckt hinter jedem Maskottchen, das Kinder sofort mögen, und es
 * wirkt, weil es dieselbe Reaktion auslöst wie ein Babygesicht.
 *
 * Das Gesicht sitzt auf einer eigenen hellen Fläche, nicht direkt auf der
 * Ballonhülle. Das ist kein Schmuck, sondern der Grund, warum die Nähte jetzt
 * sauber außen herumlaufen statt quer durchs Gesicht: Sie enden am Rand der
 * hellen Fläche. Nebenbei sind die Augen dadurch auf jeder Hüllenfarbe gleich
 * gut lesbar.
 *
 * Die Figur kommentiert, sie bewertet nicht: Bei einem Fehler schaut sie
 * aufmunternd, nie enttäuscht. Ein Maskottchen, das traurig guckt, wenn ein
 * Kind etwas nicht weiß, ist genau die Sorte Rückmeldung, die eine Lern-App
 * nicht geben sollte.
 */
export function Maskottchen({
  stimmung = 'neutral',
  gross = false,
}: {
  stimmung?: Stimmung
  gross?: boolean
}) {
  const froh = stimmung === 'begeistert' || stimmung === 'feiert'
  const huelle = froh ? '#ffd166' : '#ef476f'
  const huelleDunkel = froh ? '#e08700' : '#a3234a'
  const kennung = `pippa-${stimmung}`

  return (
    <svg
      className={gross ? 'maskottchen maskottchen--gross' : 'maskottchen'}
      viewBox="0 0 100 124"
      aria-hidden="true"
    >
      <defs>
        <radialGradient id={`${kennung}-huelle`} cx="34%" cy="26%">
          <stop offset="0%" stopColor="#fff8e7" stopOpacity="0.95" />
          <stop offset="45%" stopColor={huelle} />
          <stop offset="100%" stopColor={huelleDunkel} />
        </radialGradient>
      </defs>

      {/* Hülle: bewusst fast kreisrund, das wirkt freundlicher als eine
          tropfenförmige. */}
      <path
        d="M50 4 C77 4 94 25 94 48 C94 68 79 82 62 90 L38 90 C21 82 6 68 6 48 C6 25 23 4 50 4 Z"
        fill={`url(#${kennung}-huelle)`}
      />

      {/* Nähte. Sie laufen von oben nach außen und enden weit über dem
          Gesichtsfeld, deshalb kreuzen sie nichts mehr. */}
      <path
        d="M50 4 C34 10 24 24 22 40 M50 4 C66 10 76 24 78 40"
        stroke="rgba(0,0,0,0.12)"
        strokeWidth="1.6"
        fill="none"
        strokeLinecap="round"
      />

      {/* Das helle Gesichtsfeld */}
      <ellipse cx="50" cy="55" rx="31" ry="27" fill="#fff8e7" opacity="0.94" />

      <Gesicht stimmung={stimmung} />

      {/* Seile und Korb, klein gehalten: Der Kopf soll die Figur dominieren. */}
      <path d="M40 90 L43 101 M60 90 L57 101" stroke="#d9c49a" strokeWidth="2" fill="none" />
      <path d="M41 101 L59 101 L56 116 L44 116 Z" fill="#c98b4b" />
      <rect x="41" y="101" width="18" height="3.2" rx="1.2" fill="#a86a33" />
    </svg>
  )
}

function Gesicht({ stimmung }: { stimmung: Stimmung }) {
  const dunkel = '#2b1636'

  if (stimmung === 'begeistert' || stimmung === 'feiert') {
    return (
      <>
        {/* Lachaugen als Bögen. Wirkt sofort fröhlich, ohne Zähne oder Zunge. */}
        <path
          d="M30 54 Q39 43 48 54"
          stroke={dunkel}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
        <path
          d="M52 54 Q61 43 70 54"
          stroke={dunkel}
          strokeWidth="4"
          fill="none"
          strokeLinecap="round"
        />
        <path d="M40 63 Q50 76 60 63 Z" fill={dunkel} />
        <path d="M43 68 Q50 73 57 68 Z" fill="#ff8fab" />
        <ellipse cx="26" cy="62" rx="6" ry="4" fill="#ff8fab" opacity="0.55" />
        <ellipse cx="74" cy="62" rx="6" ry="4" fill="#ff8fab" opacity="0.55" />
      </>
    )
  }

  if (stimmung === 'staunt') {
    return (
      <>
        <g className="maskottchen__augen">
          <ellipse cx="37" cy="52" rx="10" ry="12" fill="#fff" />
          <ellipse cx="63" cy="52" rx="10" ry="12" fill="#fff" />
          <circle cx="37" cy="54" r="6.4" fill={dunkel} />
          <circle cx="63" cy="54" r="6.4" fill={dunkel} />
          <circle cx="39.4" cy="50.6" r="2.4" fill="#fff" />
          <circle cx="65.4" cy="50.6" r="2.4" fill="#fff" />
        </g>
        <ellipse cx="50" cy="70" rx="5" ry="6.5" fill={dunkel} />
      </>
    )
  }

  if (stimmung === 'trost') {
    return (
      <>
        {/* Leicht angehobene Brauen: zugewandt, nicht enttäuscht. */}
        <path
          d="M28 40 Q37 36 46 40 M54 40 Q63 36 72 40"
          stroke={dunkel}
          strokeWidth="2.6"
          fill="none"
          strokeLinecap="round"
          opacity="0.8"
        />
        <g className="maskottchen__augen">
          <ellipse cx="37" cy="54" rx="8.6" ry="8.6" fill="#fff" />
          <ellipse cx="63" cy="54" rx="8.6" ry="8.6" fill="#fff" />
          <circle cx="37" cy="55.6" r="5.2" fill={dunkel} />
          <circle cx="63" cy="55.6" r="5.2" fill={dunkel} />
          <circle cx="39" cy="52.8" r="1.9" fill="#fff" />
          <circle cx="65" cy="52.8" r="1.9" fill="#fff" />
        </g>
        {/* Kleines aufmunterndes Lächeln, kein trauriger Bogen. */}
        <path
          d="M42 70 Q50 74 58 70"
          stroke={dunkel}
          strokeWidth="3.4"
          fill="none"
          strokeLinecap="round"
        />
      </>
    )
  }

  return (
    <>
      <g className="maskottchen__augen">
        <ellipse cx="37" cy="53" rx="9.2" ry="10" fill="#fff" />
        <ellipse cx="63" cy="53" rx="9.2" ry="10" fill="#fff" />
        <circle cx="37" cy="54.6" r="5.8" fill={dunkel} />
        <circle cx="63" cy="54.6" r="5.8" fill={dunkel} />
        <circle cx="39.3" cy="51.4" r="2.2" fill="#fff" />
        <circle cx="65.3" cy="51.4" r="2.2" fill="#fff" />
      </g>
      <path
        d="M43 68 Q50 75 57 68"
        stroke={dunkel}
        strokeWidth="3.4"
        fill="none"
        strokeLinecap="round"
      />
      <ellipse cx="26" cy="62" rx="5.5" ry="3.6" fill="#ff8fab" opacity="0.4" />
      <ellipse cx="74" cy="62" rx="5.5" ry="3.6" fill="#ff8fab" opacity="0.4" />
    </>
  )
}
